import React, { PropsWithChildren } from 'react';
import { Box, Divider, Stack, SvgIcon, TextField, Typography } from '@mui/material';

import { formatDegrees, formatLengthToArchitecturalString } from '@paragon/client-tools';
import { vectorToDegrees } from '@paragon/geometry-library';
import { InlineNotification } from '@paragon/notification-tools';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import {
  DivisionLine,
  ParallelLine,
  PerpendicularLine,
  PointSlopeLine,
  PointToPointLine,
} from '../../../../resources/LineTypeIcons';
import {
  BasicInsertionInformation,
  getBasicInsertionInformation,
  isBasicInsertionModeActive,
} from '../../../store/selectors/basicInsertionSelectors';
import { getReferenceLineShortNamesByGuidForActiveComponent } from '../../../store/selectors/getReferenceLineShortNamesByGuidForActiveComponentSelector';
import { BasicInsertionFirstSelection, BasicInsertionSecondSelection } from '../../../store/slices/applicationSlice';
import { useAppSelector } from '../../hooks';

function definitionPartToString(
  part: BasicInsertionFirstSelection | BasicInsertionSecondSelection,
  referenceLineShortNamesByGuid: ReadonlyMap<string, string>,
) {
  if (!part) return 'unknown';

  switch (part.type) {
    case 'intersection': {
      const line1Guid = part.value?.line1Guid;
      const line2Guid = part.value?.line2Guid;

      const name1 = line1Guid == null ? null : (referenceLineShortNamesByGuid.get(line1Guid) ?? null);
      const name2 = line2Guid == null ? null : (referenceLineShortNamesByGuid.get(line2Guid) ?? null);

      return name1 == null || name2 == null ? 'unknown point' : `${name1} x ${name2}`;
    }

    case 'line': {
      const lineName = referenceLineShortNamesByGuid.get(part.value);
      return lineName == null ? 'unknown line' : `Line ${lineName}`;
    }

    case 'direction':
      return formatDegrees(vectorToDegrees(part.value));

    case 'offset':
      return formatLengthToArchitecturalString(part.value);

    default:
      assertExhaustiveSwitch(part);
  }
}

function getFirstSelectionStatusMessage(
  info: BasicInsertionInformation,
  referenceLineShortNamesByGuid: ReadonlyMap<string, string>,
) {
  return info.inserting
    ? info.definition.firstSelection
      ? definitionPartToString(info.definition.firstSelection, referenceLineShortNamesByGuid)
      : '<select>'
    : '<error>';
}

function getSecondSelectionStatusMessage(info: BasicInsertionInformation) {
  return info.inserting ? (info.definition.firstSelection ? '<select>' : '<waiting>') : '<error>';
}

function LineTypeExplanation(props: PropsWithChildren<{ icon: typeof SvgIcon }>) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', textWrap: 'wrap' }}>
      <SvgIcon sx={{ m: 1 }} component={props.icon} inheritViewBox />
      <Typography variant="body2">{props.children}</Typography>
    </Box>
  );
}

export function BasicInsertionProperties(props: { type: 'member' | 'referenceLine' }) {
  const basicInsertionInfo = useAppSelector(getBasicInsertionInformation);
  const referenceLineShortNamesByGuid = useAppSelector(getReferenceLineShortNamesByGuidForActiveComponent);

  if (!isBasicInsertionModeActive(basicInsertionInfo)) {
    return (
      <InlineNotification type="info" fullWidth sx={{ m: 1 }}>
        Defined by a manually edited reference line. See the geometry tab.
      </InlineNotification>
    );
  }

  if (referenceLineShortNamesByGuid == null) return null;

  const pluralType = (() => {
    switch (props.type) {
      case 'member':
        return 'members';

      case 'referenceLine':
        return 'lines';

      default:
        assertExhaustiveSwitch(props.type);
    }
  })();

  return (
    <>
      <Stack direction="column" sx={{ marginTop: 1 }} spacing={2}>
        <LineTypeExplanation icon={PointToPointLine}>
          Point-to-point {pluralType} are created by clicking two intersections.
        </LineTypeExplanation>

        <LineTypeExplanation icon={PointSlopeLine}>
          Point-slope {pluralType} are created by clicking an intersection and clicking again to set the angle.
        </LineTypeExplanation>

        <LineTypeExplanation icon={ParallelLine}>
          Parallel {pluralType} are created by clicking a line and clicking again at the desired offset.
        </LineTypeExplanation>

        <LineTypeExplanation icon={PerpendicularLine}>
          Perpendicular {pluralType} are created by clicking a line followed by an intersection.
        </LineTypeExplanation>

        <LineTypeExplanation icon={DivisionLine}>
          Division {pluralType} are created by clicking a line followed by another line.
          <Box sx={{ fontStyle: 'italic', paddingTop: 0.5, paddingLeft: 1 }}>
            Create multiple division lines with the + and - keys, or using the &quot;Quantity&quot; field in the top
            right.
          </Box>
        </LineTypeExplanation>
      </Stack>

      <Divider sx={{ marginY: 2 }} />

      <Stack direction="row" spacing={1} sx={{ marginTop: 2, justifyContent: 'center' }}>
        <TextField
          disabled
          size="small"
          variant="outlined"
          label="First Selection"
          value={getFirstSelectionStatusMessage(basicInsertionInfo, referenceLineShortNamesByGuid)}
        />

        <TextField
          disabled
          size="small"
          variant="outlined"
          label="Second Selection"
          value={getSecondSelectionStatusMessage(basicInsertionInfo)}
        />
      </Stack>
    </>
  );
}
